import {
  PRESET_INTERCHANGE_FORMAT,
  cloneValue,
  collectionKey,
  decodePayload,
  exactFields,
  frozenClone,
  opaqueId,
  savedPresetName,
  timestampRange,
  isoTimestamp,
} from "./preset-internals.js";

function decodeInterchangePayload(contract, value) {
  if (typeof contract?.migrateLegacyPayload !== "function") {
    throw new TypeError("Preset collection has no v1 interchange payload migrator.");
  }
  const migrated = contract.migrateLegacyPayload(cloneValue(value));
  return decodePayload(contract, migrated);
}

export function decodePresetInterchange(value, contract, expectedCollectionKey) {
  if (!exactFields(value, ["collectionKey", "exportedAt", "format", "presets", "selectedPresetId"])) {
    throw new TypeError("Preset interchange fields must match fishystuff-user-presets.v1.");
  }
  if (value.format !== PRESET_INTERCHANGE_FORMAT) {
    throw new TypeError(`Preset interchange format must be ${PRESET_INTERCHANGE_FORMAT}.`);
  }
  if (collectionKey(value.collectionKey) !== expectedCollectionKey) {
    throw new TypeError(`Preset interchange collection must be ${expectedCollectionKey}.`);
  }
  const exportedAt = isoTimestamp(value.exportedAt, "Preset export time");
  if (typeof value.selectedPresetId !== "string") {
    throw new TypeError("Selected preset ID must be text.");
  }
  if (!Array.isArray(value.presets)) {
    throw new TypeError("Preset interchange presets must be an array.");
  }
  const seen = new Set();
  const presets = value.presets.map((raw, index) => {
    if (!exactFields(raw, ["createdAt", "id", "name", "payload", "updatedAt"])) {
      throw new TypeError(`Imported preset ${index + 1} fields must match v1.`);
    }
    const id = opaqueId(raw.id, "Imported preset ID");
    if (seen.has(id)) {
      throw new TypeError(`Imported preset ID ${id} is duplicated.`);
    }
    seen.add(id);
    return {
      id,
      name: savedPresetName(raw.name, "Imported preset name"),
      payload: decodeInterchangePayload(contract, raw.payload),
      ...timestampRange(
        raw.createdAt,
        raw.updatedAt,
        `Imported preset ${index + 1}`,
      ),
    };
  });
  if (value.selectedPresetId && !seen.has(value.selectedPresetId)) {
    throw new TypeError("Selected preset ID must reference an imported preset.");
  }
  return frozenClone({
    format: PRESET_INTERCHANGE_FORMAT,
    collectionKey: expectedCollectionKey,
    exportedAt,
    selectedPresetId: value.selectedPresetId,
    presets,
  });
}
